import FileSVG from '@public/icons/file.svg';
import { AxiosError } from 'axios';
import classNames from 'classnames';
import DeleteSVG from 'public/icons/bin.svg';
import { useCallback, useMemo, useState } from 'react';
import { type DropzoneOptions, type FileRejection, useDropzone } from 'react-dropzone';
import type { ApiResponse } from '@/shared/api/lib/types';
import { useLocale } from '@/shared/hooks/use-locale';
import {
    getAllowedFormatsPlaceholder,
    getDropzoneAccept,
    getInvalidFileTypeMessage,
    isAllowedFileType,
} from '@/shared/lib/utils/files/is-allowed-file-type';
import { MAX_FILE_SIZE_BYTES, getMaxFileSizeMessage } from '@/shared/lib/utils/files/max-file-size';
import { formatFileName } from '@/shared/lib/utils/format-file-name';
import Typografed from '../Typografed';

function getUploadErrorMessage(error: unknown, maxFileSizeMessage: string): string {
    if (error instanceof AxiosError) {
        if (error.response?.status === 413) {
            return maxFileSizeMessage;
        }

        const data = error.response?.data as ApiResponse | undefined;
        const errorsMessage = data?.errors?.flat().filter(Boolean).join(' ');
        if (errorsMessage) return errorsMessage;
    }
    if (error instanceof Error && error.message) {
        return error.message;
    }
    return 'Не удалось загрузить файл';
}

type FileDropzoneProps = {
    label: string;
    fieldName: string;
    formik: any;
    allowedFileTypes: string[];
    validateMime?: boolean;
    onUpload: (files: File[]) => Promise<string[] | void>;
};

const FileDropzone: React.FC<FileDropzoneProps> = ({
    label,
    fieldName,
    formik,
    allowedFileTypes,
    validateMime = false,
    onUpload,
}) => {
    const locale = useLocale();
    const currentLinks: string[] = formik.values[fieldName] || [];
    const [isLoading, setIsLoading] = useState(false);
    const [fileError, setFileError] = useState<string | null>(null);
    const invalidFileTypeMessage = getInvalidFileTypeMessage(allowedFileTypes);
    const maxFileSizeMessage = getMaxFileSizeMessage(locale);

    const fileTypeOptions = useMemo(() => ({ validateMime }), [validateMime]);

    const showError = useCallback(
        (message: string) => {
            setFileError(message);
            formik.setFieldTouched(fieldName, true, false);
        },
        [fieldName, formik],
    );

    const onDrop = useCallback(
        async (acceptedFiles: File[], fileRejections: FileRejection[]) => {
            if (fileRejections.length > 0) {
                const rejectionErrors = fileRejections[0]?.errors ?? [];
                const isTypeError = rejectionErrors.some((error) => error.code === 'file-invalid-type');
                const isSizeError = rejectionErrors.some((error) => error.code === 'file-too-large');
                const msg = isSizeError
                    ? maxFileSizeMessage
                    : isTypeError
                      ? invalidFileTypeMessage
                      : rejectionErrors.find((error) => error.message)?.message || invalidFileTypeMessage;
                showError(msg);
                return;
            }

            if (acceptedFiles.length === 0) return;

            const oversizedFiles = acceptedFiles.filter((file) => file.size > MAX_FILE_SIZE_BYTES);
            if (oversizedFiles.length > 0) {
                showError(maxFileSizeMessage);
                return;
            }

            const invalidFiles = acceptedFiles.filter(
                (file) => !isAllowedFileType(file, allowedFileTypes, fileTypeOptions),
            );
            if (invalidFiles.length > 0) {
                showError(invalidFileTypeMessage);
                return;
            }

            setIsLoading(true);
            setFileError(null);
            formik.setFieldError(fieldName, undefined);
            try {
                const uploadedLinks = await onUpload(acceptedFiles);

                if (!uploadedLinks) return;

                const latest = (formik.values[fieldName] as string[] | undefined) || [];
                const updated = [...latest, ...uploadedLinks];

                await formik.setFieldValue(fieldName, updated, true);
                await formik.setFieldTouched(fieldName, true, false);
                formik.setFieldError(fieldName, undefined);
                setFileError(null);
            } catch (e) {
                const errorMessage = getUploadErrorMessage(e, maxFileSizeMessage);
                showError(errorMessage);
                formik.setFieldError(fieldName, errorMessage);
            } finally {
                setIsLoading(false);
            }
        },
        [
            allowedFileTypes,
            fieldName,
            fileTypeOptions,
            formik,
            invalidFileTypeMessage,
            maxFileSizeMessage,
            onUpload,
            showError,
        ],
    );

    const formikError = formik.touched[fieldName] && formik.errors[fieldName];
    const displayError = fileError || formikError;
    const hasError = Boolean(displayError);

    const removeFile = (linkToRemove: string, event: React.MouseEvent) => {
        event.stopPropagation();
        const prev = (formik.values[fieldName] as string[] | undefined) || [];
        formik.setFieldValue(
            fieldName,
            prev.filter((link) => link !== linkToRemove),
        );
        formik.setFieldTouched(fieldName, true, false);
    };

    const { getRootProps, getInputProps } = useDropzone({
        onDrop,
        disabled: isLoading,
        accept: getDropzoneAccept(allowedFileTypes) as DropzoneOptions['accept'],
        maxSize: MAX_FILE_SIZE_BYTES,
        validator: (file) => {
            if (file.size > MAX_FILE_SIZE_BYTES) {
                return { code: 'file-too-large', message: maxFileSizeMessage };
            }
            if (isAllowedFileType(file, allowedFileTypes, fileTypeOptions)) {
                return null;
            }
            return { code: 'file-invalid-type', message: invalidFileTypeMessage };
        },
        multiple: true,
    });

    return (
        <div className="apply-registration-input-block text-s">
            <div className="apply-registration-input-name text-s">{label}</div>
            <div
                className={classNames('dropzone-wrapper', {
                    'is-active': currentLinks.length > 0,
                    'is-loader': isLoading,
                })}
                {...getRootProps()}
            >
                <div className={classNames('dropzone', { 'is-error': hasError })}>
                    <input {...getInputProps()} />
                    <div className="download-loader-wrapper">
                        <div className="download-loader"></div>
                    </div>
                    <FileSVG className="dropzone-file-icon" />
                    {currentLinks.length > 0 ? (
                        <div className="dropzone-file-list">
                            {currentLinks.map((link, index) => (
                                <div key={index} className="dropzone-file-item">
                                    <div className="dropzone-file-name">{formatFileName(link)}</div>
                                    <button
                                        className="dropzone-delete-button"
                                        aria-label="Удалить файл"
                                        onClick={(e) => removeFile(link, e)}
                                    >
                                        <DeleteSVG className="dropzone-delete-icon" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="dropzone-placeholder text-s">
                            <Typografed>{getAllowedFormatsPlaceholder(allowedFileTypes)}</Typografed>
                        </div>
                    )}
                </div>
            </div>
            {hasError && <div className="app-message text-xs">{displayError}</div>}
        </div>
    );
};

export default FileDropzone;
